import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { API_URL } from '../config';
import AdSense from './AdSense';
import { sanitizeErrorMessage, validateInputSize } from '../utils/security';

// Set to true when you integrate Google AdSense
const SHOW_ADS = true;

const ComparisonTool = () => {
  const [data1, setData1] = useState('');
  const [data2, setData2] = useState('');
  const [type, setType] = useState('json');
  const [differences, setDifferences] = useState([]);
  const [isEqual, setIsEqual] = useState(null);
  const [error, setError] = useState('');
  const [formatted1, setFormatted1] = useState('');
  const [formatted2, setFormatted2] = useState('');

  // Function to determine which lines need highlighting
  const getLineHighlights = (jsonStr, differences, side) => {
    if (!jsonStr || differences.length === 0) return {};
    
    const lines = jsonStr.split('\n');
    const lineHighlights = {};
    
    differences.forEach(diff => {
      const path = diff.path;
      
      // Extract the last key from the path
      let lastKey = path;
      if (path.includes('.')) {
        const parts = path.split('.');
        lastKey = parts[parts.length - 1];
      }
      // Handle array indices like "items[0]"
      if (lastKey.includes('[')) {
        lastKey = lastKey.split('[')[0];
      }
      
      // Clean the key (remove quotes if present)
      const cleanKey = lastKey.replace(/^["']|["']$/g, '');
      
      // Determine which value to look for based on the side
      let targetValue = null;
      if (side === 1) {
        targetValue = diff.value1;
      } else {
        targetValue = diff.value2;
      }
      
      // Find lines that contain this specific key-value pair
      lines.forEach((line, index) => {
        const lineNum = index + 1;
        let shouldHighlight = false;
        let highlightType = '';
        
        // More precise matching: the line should contain the key with quotes followed by colon
        // This ensures we match "key": not just the word "key" anywhere
        const escapedKey = cleanKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const keyPattern = new RegExp(`"${escapedKey}"\\s*:`, 'g');
        const hasKey = keyPattern.test(line);
        
        if (!hasKey) {
          // Reset regex lastIndex for next iteration
          keyPattern.lastIndex = 0;
          return; // Skip if key not found on this line
        }
        // Reset regex lastIndex after successful match
        keyPattern.lastIndex = 0;
        
        // Determine if this difference should be highlighted on this side
        // Use side-based highlighting: JSON 1 = green, JSON 2 = red
        if (diff.type === 'missing_in_second' && side === 1) {
          // Value exists in side 1 but not in side 2 - highlight side 1 (green)
          shouldHighlight = true;
          highlightType = side === 1 ? 'json1-diff' : 'json2-diff';
        } else if (diff.type === 'missing_in_first' && side === 2) {
          // Value exists in side 2 but not in side 1 - highlight side 2 (red)
          shouldHighlight = true;
          highlightType = side === 1 ? 'json1-diff' : 'json2-diff';
        } else if (diff.type === 'missing_in_second' && side === 2) {
          // Value doesn't exist in side 2 - don't highlight (it's not there)
          shouldHighlight = false;
        } else if (diff.type === 'missing_in_first' && side === 1) {
          // Value doesn't exist in side 1 - don't highlight (it's not there)
          shouldHighlight = false;
        } else if (diff.type === 'value_mismatch' || diff.type === 'type_mismatch') {
          // Both sides have the key but different values - highlight both sides
          // JSON 1 gets green, JSON 2 gets red
          shouldHighlight = true;
          highlightType = side === 1 ? 'json1-diff' : 'json2-diff';
        }
        
        if (shouldHighlight && highlightType) {
          // Always set the highlight type based on side
          lineHighlights[lineNum] = highlightType;
        }
      });
    });
    
    return lineHighlights;
  };

  const handleCompare = async () => {
    // SECURITY: Client-side input validation
    if (!data1 || !data1.trim() || !data2 || !data2.trim()) {
      setError('Please enter data in both fields to compare');
      return;
    }

    // SECURITY: Validate input sizes
    const sizeCheck1 = validateInputSize(data1, 10);
    const sizeCheck2 = validateInputSize(data2, 10);
    if (!sizeCheck1.valid) {
      setError(sizeCheck1.error);
      return;
    }
    if (!sizeCheck2.valid) {
      setError(sizeCheck2.error);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/compare/`, {
        data1,
        data2,
        type
      });
      
      setIsEqual(response.data.equal);
      setDifferences(response.data.differences || []);
      setFormatted1(response.data.formatted1 || '');
      setFormatted2(response.data.formatted2 || '');
      setError('');
    } catch (err) {
      // SECURITY: Sanitize error message
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsEqual(null);
      setDifferences([]);
      setFormatted1('');
      setFormatted2('');
    }
  };

  // Function to determine which lines need highlighting for XML
  const getXmlLineHighlights = (xmlStr, differences, side) => {
    if (!xmlStr || differences.length === 0) return {};
    
    const lines = xmlStr.split('\n');
    const lineHighlights = {};
    
    differences.forEach(diff => {
      const path = diff.path;
      
      // Determine which value to look for based on the side
      let targetValue = null;
      if (side === 1) {
        targetValue = diff.value1;
      } else {
        targetValue = diff.value2;
      }
      
      // Find lines that contain this path or value
      lines.forEach((line, index) => {
        const lineNum = index + 1;
        let shouldHighlight = false;
        let highlightType = '';
        
        // Extract tag name, attribute, or element from path
        let searchPattern = '';
        let isAttribute = false;
        
        if (path.includes('@')) {
          // Attribute: @attr or tag@attr or path/tag@attr
          isAttribute = true;
          const parts = path.split('@');
          searchPattern = parts[parts.length - 1]; // Get the attribute name
        } else if (path.includes('[')) {
          // Array index: tag[0] or path/tag[0]
          const tagName = path.split('[')[0].split('/').pop();
          searchPattern = tagName;
        } else if (path.includes('/')) {
          // Nested path: path/tag
          const tagName = path.split('/').pop();
          searchPattern = tagName;
        } else {
          // Simple tag name
          searchPattern = path;
        }
        
        // Check if line contains the search pattern
        let hasPattern = false;
        if (isAttribute) {
          // For attributes, look for attribute="value" or attribute='value'
          hasPattern = line.includes(`${searchPattern}="`) || 
                      line.includes(`${searchPattern}='`) ||
                      (targetValue && line.includes(`${searchPattern}="${targetValue}"`)) ||
                      (targetValue && line.includes(`${searchPattern}='${targetValue}'`));
        } else {
          // For tags, look for opening/closing tags or text content
          hasPattern = line.includes(`<${searchPattern}`) || 
                      line.includes(`</${searchPattern}`) ||
                      line.includes(`<${searchPattern}>`) ||
                      (targetValue && typeof targetValue === 'string' && line.includes(targetValue));
        }
        
        if (!hasPattern) return;
        
        // Determine highlight type and if it applies to this side
        if (diff.type === 'missing_in_second' && side === 1) {
          shouldHighlight = true;
          highlightType = 'json1-diff';
        } else if (diff.type === 'missing_in_first' && side === 2) {
          shouldHighlight = true;
          highlightType = 'json2-diff';
        } else if (diff.type === 'missing_in_second' && side === 2) {
          shouldHighlight = false;
        } else if (diff.type === 'missing_in_first' && side === 1) {
          shouldHighlight = false;
        } else if (diff.type === 'value_mismatch' || diff.type === 'type_mismatch' || 
                   diff.type === 'tag_mismatch' || diff.type === 'text_mismatch') {
          // All mismatch types - highlight both sides
          // For mismatches, check if the value matches this side
          let valueMatches = true;
          if (targetValue !== null && targetValue !== undefined) {
            if (isAttribute) {
              // Check if attribute value matches
              const attrPattern = new RegExp(`${searchPattern}="([^"]*)"|${searchPattern}='([^']*)'`, 'i');
              const match = line.match(attrPattern);
              if (match) {
                const attrValue = match[1] || match[2];
                valueMatches = attrValue === targetValue || attrValue.includes(targetValue);
              }
            } else if (diff.type === 'text_mismatch') {
              // For text mismatches, check if line contains the text
              valueMatches = line.includes(targetValue);
            } else {
              // For tag mismatches, the tag name itself is the difference
              valueMatches = true;
            }
          }
          shouldHighlight = valueMatches;
          highlightType = side === 1 ? 'json1-diff' : 'json2-diff';
        }
        
        if (shouldHighlight && highlightType) {
          lineHighlights[lineNum] = highlightType;
        }
      });
    });
    
    return lineHighlights;
  };

  // Calculate line highlights
  const lineHighlights1 = useMemo(() => 
    type === 'json' ? getLineHighlights(formatted1, differences, 1) : 
    type === 'xml' ? getXmlLineHighlights(formatted1, differences, 1) : {}, 
    [formatted1, differences, type]
  );
  
  const lineHighlights2 = useMemo(() => 
    type === 'json' ? getLineHighlights(formatted2, differences, 2) : 
    type === 'xml' ? getXmlLineHighlights(formatted2, differences, 2) : {}, 
    [formatted2, differences, type]
  );

  // Custom renderer component for JSON
  const renderHighlightedJson = (jsonStr, lineHighlights) => {
    const lines = jsonStr.split('\n');
    return (
      <div className="highlighted-json">
        {lines.map((line, index) => {
          const lineNum = index + 1;
          const highlightType = lineHighlights[lineNum];
          let className = 'json-line';
          
          if (highlightType === 'json1-diff') {
            className += ' diff-json1';
          } else if (highlightType === 'json2-diff') {
            className += ' diff-json2';
          }
          
          return (
            <div key={index} className={className}>
              <span className="line-number">{lineNum}</span>
              <span className="line-content">{line || ' '}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom renderer component for XML
  const renderHighlightedXml = (xmlStr, lineHighlights) => {
    const lines = xmlStr.split('\n');
    return (
      <div className="highlighted-json">
        {lines.map((line, index) => {
          const lineNum = index + 1;
          const highlightType = lineHighlights[lineNum];
          let className = 'json-line';
          
          if (highlightType === 'json1-diff') {
            className += ' diff-json1';
          } else if (highlightType === 'json2-diff') {
            className += ' diff-json2';
          }
          
          return (
            <div key={index} className={className}>
              <span className="line-number">{lineNum}</span>
              <span className="line-content">{line || ' '}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="comparison-container">
      <div className="comparison-header">
        <h2>Data Comparison Tool</h2>
        <select value={type} onChange={(e) => setType(e.target.value)} className="type-select">
          <option value="json">JSON</option>
          <option value="xml">XML</option>
        </select>
        <button onClick={handleCompare} className="compare-btn">
          Compare
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {isEqual !== null && (
        <div className={`result-banner ${isEqual ? 'equal' : 'different'}`}>
          {isEqual ? '✓ Data structures are identical' : '✗ Data structures differ'}
        </div>
      )}

      <div className="comparison-inputs">
        <div className="input-panel">
          <h3>Data 1</h3>
          <textarea
            value={data1}
            onChange={(e) => setData1(e.target.value)}
            placeholder={type === 'json' ? '{"key": "value"}' : '<root><item>value</item></root>'}
            className="comparison-textarea"
          />
        </div>
        <div className="input-panel">
          <h3>Data 2</h3>
          <textarea
            value={data2}
            onChange={(e) => setData2(e.target.value)}
            placeholder={type === 'json' ? '{"key": "value"}' : '<root><item>value</item></root>'}
            className="comparison-textarea"
          />
        </div>
      </div>

      {/* In-content Ad - Only visible when SHOW_ADS is true and comparison is done */}
      {SHOW_ADS && isEqual !== null && (
        <div className="in-content-ad">
          <AdSense
            adFormat="auto"
            style={{ width: '100%', maxWidth: '728px', height: '90px', margin: '2rem auto', display: 'block' }}
            className="ad-incontent"
          />
        </div>
      )}

      {formatted1 && formatted2 && type === 'json' && (
        <div className="json-diff-view">
          <div className="diff-panel">
            <h3>JSON 1</h3>
            <div className="json-diff-content">
              {renderHighlightedJson(formatted1, lineHighlights1)}
            </div>
          </div>
          <div className="diff-panel">
            <h3>JSON 2</h3>
            <div className="json-diff-content">
              {renderHighlightedJson(formatted2, lineHighlights2)}
            </div>
          </div>
        </div>
      )}

      {formatted1 && formatted2 && type === 'xml' && (
        <div className="json-diff-view">
          <div className="diff-panel">
            <h3>XML 1</h3>
            <div className="json-diff-content">
              {renderHighlightedXml(formatted1, lineHighlights1)}
            </div>
          </div>
          <div className="diff-panel">
            <h3>XML 2</h3>
            <div className="json-diff-content">
              {renderHighlightedXml(formatted2, lineHighlights2)}
            </div>
          </div>
        </div>
      )}

      {differences.length > 0 && (
        <div className="differences-section">
          <h3>Differences ({differences.length})</h3>
          <div className="differences-list">
            {differences.map((diff, index) => (
              <div key={index} className="difference-item">
                <div className="difference-path"><strong>Path:</strong> {diff.path}</div>
                <div className="difference-type"><strong>Type:</strong> {diff.type}</div>
                <div className="difference-values">
                  <div className="value-left">
                    <strong>Value 1:</strong> {JSON.stringify(diff.value1)}
                  </div>
                  <div className="value-right">
                    <strong>Value 2:</strong> {JSON.stringify(diff.value2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonTool;
